import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaArrowLeft } from "react-icons/fa";

import {
  EmptyState,
  FixturesShowcase,
  SectionCard,
  StatCard,
  StatusPill,
} from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { SchoolOpsHeader } from "context/school-ops-header-context";
import { useAuth } from "context/auth-context";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import { SchoolMatchRostersPanel } from "pages/app/workspace/sections/school-match-rosters-panel";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import {
  SchoolMatchesSectionActionButton,
  SchoolMatchesSectionContentStack,
} from "pages/app/workspace/sections/styles/school-matches-section.inline.styles";
import {
  SchoolHubContentStack,
  SchoolHubTabButton,
  SchoolHubTabRow,
} from "pages/app/workspace/styles/school-hub.page.inline.styles";
import { SchoolRosterProfileSecondaryButton } from "pages/app/workspace/styles/school-roster-profile.page.inline.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";

type MatchHubTab = "overview" | "rosters" | "score";

const MATCH_HUB_TABS: Array<{ id: MatchHubTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "rosters", label: "Rosters" },
  { id: "score", label: "Score" },
];

const parseMatchHubTab = (search: string): MatchHubTab => {
  const tab = new URLSearchParams(search).get("tab") as MatchHubTab | null;
  if (tab === "rosters" || tab === "score" || tab === "overview") {
    return tab;
  }
  return "overview";
};

const SchoolMatchHubPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const history = useHistory();
  const location = useLocation<{ backTo?: string }>();
  const { portalAccess } = useAuth();
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const effectivePortalAccess = currentUser?.portal_access || portalAccess;
  const { schoolId } = useScopedSchoolSelection({
    portalAccess: effectivePortalAccess,
  });

  const activeTab = useMemo(() => parseMatchHubTab(location.search), [location.search]);
  const backTo = location.state?.backTo || "/workspace/schedule";
  const hubBackTo = `/workspace/school-matches/${matchId}`;

  const matchQuery = useQuery({
    queryKey: ["workspace-school-match-hub", matchId],
    queryFn: () => workspaceApi.schoolMatches.get(matchId),
    enabled: Boolean(matchId),
  });

  const match = matchQuery.data;
  const matchLabel =
    match?.competition_name ||
    `${match?.home_school_team?.name || "Home"} vs ${match?.away_school_team?.name || "Away"}`;
  const canManageSchoolMatches = hasPortalAction(effectivePortalAccess, "school_matches.manage");
  const canManageRosters = hasPortalAction(effectivePortalAccess, "school_match_rosters.manage");
  const visibleTabs = useMemo(
    () =>
      MATCH_HUB_TABS.filter((tab) => {
        if (tab.id === "rosters") {
          return Boolean(canManageRosters || match);
        }
        return true;
      }),
    [canManageRosters, match],
  );

  const setTab = useCallback(
    (tab: MatchHubTab) => {
      history.replace({
        pathname: location.pathname,
        search: tab === "overview" ? "" : `?tab=${tab}`,
        state: location.state,
      });
    },
    [history, location.pathname, location.state],
  );

  const headerActions = useMemo(
    () => (
      <SchoolRosterProfileSecondaryButton onClick={() => history.push(backTo)} type="button">
        <FaArrowLeft size={12} />
        Back
      </SchoolRosterProfileSecondaryButton>
    ),
    [backTo, history],
  );

  const headerTabs = useMemo(
    () =>
      match ? (
        <SchoolHubTabRow>
          {visibleTabs.map((tab) => (
            <SchoolHubTabButton
              $active={activeTab === tab.id}
              key={tab.id}
              onClick={() => setTab(tab.id)}
              type="button"
            >
              {tab.label}
            </SchoolHubTabButton>
          ))}
        </SchoolHubTabRow>
      ) : undefined,
    [activeTab, match, setTab, visibleTabs],
  );

  return (
    <AppLayout route="/workspace/schedule">
      <SchoolOpsHeader actions={headerActions} tabs={headerTabs} title={matchLabel || "Match"} />

      <SchoolHubContentStack>
        {match ? (
          <SchoolHubContentStack>
            <div className="grid gap-4 md:grid-cols-4">
              <StatCard label="Status" value={<StatusPill value={match.status} />} />
              <StatCard label="Sport" value={match.sport_name || "Unknown"} />
              <StatCard label="Scheduled" value={formatDateTime(match.scheduled_at)} />
              <StatCard label="Venue" value={match.venue_name || "Venue pending"} />
            </div>

            {activeTab === "overview" ? (
              <SectionCard subtitle="Fixture context and live links for this match." title="Match Overview">
                <SchoolMatchesSectionContentStack>
                  <FixturesShowcase
                    emptyDescription="Fixture details will appear here once the match loads."
                    emptyTitle="No fixture loaded"
                    fixtures={match ? [match] : []}
                    isLoading={matchQuery.isLoading}
                  />
                </SchoolMatchesSectionContentStack>
              </SectionCard>
            ) : null}

            {activeTab === "rosters" ? (
              <SchoolMatchRostersPanel
                listBackTo={hubBackTo}
                matchId={matchId}
                portalAccess={effectivePortalAccess}
                rosterProfileBackTo={hubBackTo}
                schoolId={schoolId}
              />
            ) : null}

            {activeTab === "score" ? (
              <SectionCard
                subtitle="Launch the live scorer for this fixture without leaving the match workspace."
                title="Scoresheet"
              >
                <SchoolMatchesSectionActionButton
                  onClick={() =>
                    history.push({
                      pathname: `/workspace/school-scoresheets/${matchId}`,
                      state: {
                        backTo: `${hubBackTo}${activeTab === "score" ? "?tab=score" : ""}`,
                      },
                    })
                  }
                  type="button"
                >
                  {canManageSchoolMatches ? "Launch scorer" : "Review scoresheet"}
                </SchoolMatchesSectionActionButton>
              </SectionCard>
            ) : null}
          </SchoolHubContentStack>
        ) : matchQuery.isLoading ? null : (
          <EmptyState
            description="This match could not be loaded for the current school scope."
            title="Match not found"
          />
        )}
      </SchoolHubContentStack>
    </AppLayout>
  );
};

export default SchoolMatchHubPage;
